import React, { useEffect, useState } from "react";
import { axios } from "../../lib/axios";
import { errorMessage } from "../../lib/download";
import FileField from "../ui/FileField";
import { t } from "../../i18n";

// Company details printed on payslips and exports (owners only; the server enforces it).
const CompanySettingsModal = ({ onClose }) => {
  const [form, setForm] = useState({ company_name: "", company_address: "", company_phone: "" });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [removeLogo, setRemoveLogo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios
      .get("/settings/company", { params: { with_logo: 1 } })
      .then(({ data }) => {
        setForm({
          company_name: data.company_name || "",
          company_address: data.company_address || "",
          company_phone: data.company_phone || "",
        });
        setLogoPreview(data.logo_data_uri || "");
      })
      .catch(() => {});
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      if (logo) body.append("logo", logo);
      if (removeLogo) body.append("remove_logo", "1");
      await axios.post("/settings/company", body);
      setSaved(true);
    } catch (err) {
      setError(await errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className="modal-overlay">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="company-title"
        className="modal-panel max-w-lg p-6"
      >
        <h2 id="company-title" className="text-xl font-bold text-brand-600">
          {t("إعدادات الشركة")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          {t("تظهر هذه البيانات في كشوف الرواتب وملفات التصدير.")}
        </p>

        <label htmlFor="co-name" className="mt-4 block text-sm font-medium text-slate-700">
          {t("اسم الشركة")}
        </label>
        <input
          id="co-name"
          required
          maxLength={120}
          className="field mt-1"
          value={form.company_name}
          onChange={set("company_name")}
        />

        <label htmlFor="co-address" className="mt-3 block text-sm font-medium text-slate-700">
          {t("العنوان")}
        </label>
        <input
          id="co-address"
          maxLength={255}
          className="field mt-1"
          value={form.company_address}
          onChange={set("company_address")}
        />

        <label htmlFor="co-phone" className="mt-3 block text-sm font-medium text-slate-700">
          {t("الهاتف")}
        </label>
        <input
          id="co-phone"
          maxLength={40}
          className="field mt-1"
          value={form.company_phone}
          onChange={set("company_phone")}
        />

        {logoPreview && !logo && !removeLogo && (
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-slate-200 bg-card p-3 shadow-sm">
            <img
              src={logoPreview}
              alt={t("الشعار الحالي")}
              className="h-14 w-14 shrink-0 rounded-lg object-contain ring-1 ring-slate-200"
            />
            <span className="flex-1 text-sm font-medium text-slate-700">{t("الشعار الحالي")}</span>
            <button
              type="button"
              onClick={() => setRemoveLogo(true)}
              className="shrink-0 rounded-lg px-3 py-2 text-sm font-medium text-red-700 ring-1 ring-red-200 hover:bg-red-50"
            >
              {t("إزالة الشعار")}
            </button>
          </div>
        )}
        {removeLogo && (
          <p className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            {t("سيُزال الشعار عند الحفظ.")}
            <button
              type="button"
              onClick={() => setRemoveLogo(false)}
              className="font-medium underline underline-offset-2"
            >
              {t("تراجع")}
            </button>
          </p>
        )}
        <div className="mt-3">
          <FileField
            id="co-logo"
            name="logo"
            label={t("الشعار (PNG أو JPG حتى 1 ميجابايت)")}
            file={logo}
            onChange={(file) => {
              setLogo(file);
              if (file) setRemoveLogo(false);
            }}
            types={["image/png", "image/jpeg"]}
            maxBytes={1024 * 1024}
            hint={t("PNG أو JPG، بحد أقصى 1 ميجابايت")}
          />
        </div>

        {error && (
          <p role="alert" className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {error}
          </p>
        )}
        {saved && (
          <p role="status" className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
            {t("تم الحفظ.")}
          </p>
        )}

        <div className="mt-6 flex justify-start gap-3">
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-brand-600 px-4 py-2 font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
          >
            {busy ? t("جاري المعالجة...") : t("حفظ")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-card px-4 py-2 font-medium text-slate-700 hover:bg-slate-100"
          >
            {t("إغلاق")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanySettingsModal;
